import './tab-bar.css';

import { ITabBarData } from './tab-bar.interface';
import { ArrowScroller } from '../ArrowScroller/arrow-scroller';
import { LocalizationService } from '../../../services/localization-service';
import { Util } from '../../../utils/util';

/**
 * TabBar is a UI component that renders a horizontally scrollable set of tabs.
 * It uses an ArrowScroller to enable overflow scrolling with active tab highlighting.
 */
export class TabBar {
    private readonly _item: ITabBarData;
    private readonly _arrowScroller: ArrowScroller;

    /**
    * Constructs a new instance of the TabBar component.
    * @param {ITabBarData} item - The configuration for the tab bar, including buttons and metadata.
    * @param {LocalizationService} [localizationService] - Localizes the scroll arrows' labels.
    */
    constructor(item: ITabBarData, localizationService?: LocalizationService) {
        this._item = item;
        const element = this._createElement();
        this._arrowScroller = new ArrowScroller(element, { localizationService });
    }

    /**
     * Creates the root tab container element with tab buttons.
     * Each button sets its own text, attributes, event handler, and accessibility roles.
     * 
     * @private
     * @returns {HTMLElement} The constructed tab bar container with tab buttons.
     */
    private _createElement(): HTMLElement {
        const parentDiv = document.createElement("div");
        parentDiv.classList.add("tabs-bar");
        parentDiv.setAttribute("role", "tablist");
        Util.addTabListArrowNavigation(parentDiv);

        for (const tab of this._item.tabs) {
            const button = document.createElement("button");
            button.id = `${this._item.buttonId}_${tab.id}`;
            button.classList.add("tab-bar-tab");
            button.setAttribute("role", "tab");
            button.ariaSelected = String(!!tab.isSelected);
            button.tabIndex = 0;
            button.innerText = tab.text;

            if (tab.isSelected)
                button.classList.add("active");

            if (tab.ltag)
                button.setAttribute("ltag", tab.ltag);

            if (tab.ltagj)
                button.setAttribute("ltagj", tab.ltagj);

            button.onclick = (event: MouseEvent) => {
                button.dataset.humanClick = String(event.isTrusted);

                this._setActiveTabOnClick(button.id);
                if (tab.function)
                    tab.function();
            };

            button.addEventListener("focus", () => {
                button.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
            });

            try {
                parentDiv.appendChild(button);
            } catch (ex) {
                console.log(ex);
            }
        }
        return parentDiv;
    }

    /**
     * Gets the root scrollable tab bar element (managed by ArrowScroller).
     * 
     * @returns {HTMLElement} The scrollable tab bar container.
     */
    public get element(): HTMLElement {
        return this._arrowScroller.element;
    }

    /**
     * Handles tab activation when a tab button is clicked.
     * Removes the `active` class from all tabs and applies it to the clicked tab.
     * Falls back to the first tab if the target isn't found.
     * 
     * @private
     * @param {string} id - The ID of the tab button that was clicked.
     */
    private readonly _setActiveTabOnClick = (id: string): void => {
        const tabs = Array.from(this.element.querySelectorAll(".tab-bar-tab")) as HTMLElement[];
        for (const tab of tabs) {
            tab.classList.remove("active");
            tab.ariaSelected = "false";
        }

        const tab = document.getElementById(id);
        if (tab) {
            tab.classList.add("active");
            tab.ariaSelected = "true";
            tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        } else {
            const defaultTab = tabs[0];
            if (defaultTab) {
                defaultTab.classList.add("active");
                defaultTab.ariaSelected = "true";
                defaultTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
    }
}